/**
 * useDashboardData — loads everything the redesigned (Phase 2B) Dashboard
 * needs, in parallel, using ONLY the authoritative sources approved in
 * DASHBOARD_DATA_CONTRACT.md §1.
 *
 * Deliberate non-goals:
 *   • No wiring to /api/questions, /api/insights, /api/monthly-brief, or
 *     /api/weekly-brief as a Next-Best-Action or Financial-Briefing source
 *     (Amendments 0.2b.1 and 0.2b.2).
 *   • No client-side derivation of essentiality, on-track, tone, or any
 *     other financial judgement (Amendment 6).
 *   • No new backend endpoints (Amendment 12 for Phase 2A remains in
 *     spirit for Phase 2B: no backend changes).
 *
 * Timezone handling delegates to `lib/dateRange.js:isoLocal()`, the app's
 * canonical resolver (browser local time, YYYY-MM-DD calendar). No
 * section-specific fallback logic (Amendment 0.2b.8).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/context/AuthContext';
import { isoLocal } from '@/lib/dateRange';
import { readSnapshot, writeSnapshot } from '@/lib/briefingCache';
import { useCurrencyContext } from '@/hooks/useCurrencyContext';
import { periodTotals, mergeExcluded } from '@/lib/interimPolicy';
import {
  readUrlParamAndPersist, isDemoEnabled,
  DEMO_BRIEFING_UPDATES, DEMO_ATTENTION_ITEMS, DEMO_SNAPSHOT_PATCH,
  DEMO_BREAKDOWN_THIS_MONTH, DEMO_BREAKDOWN_PRIOR, DEMO_TREND_6M,
  DEMO_REFLECTION_QUESTION,
} from '@/lib/dashboardDemo';

const API = process.env.REACT_APP_BACKEND_URL;

/** Utility: authenticated axios GET. */
async function authedGet(path) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const res = await axios.get(`${API}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return res.data;
}

/**
 * Aggregate a 6-month income vs expenses series in the user's local
 * calendar (Amendment 0.2b.6 locks the window to 6 months).
 *
 * Phase 84.22.9 (Wave B2) — takes a `CurrencyContext` (from
 * `useCurrencyContext()`) instead of raw maps. Each transaction is
 * converted to base via `ctx.toBase(amount, ctx.currencyOfAccount(t.account_id))`
 * before being added to the monthly bucket. Any future currency work
 * (adding a new provider, changing the fallback behaviour, adding
 * stale-rate warnings) lands in ONE place — the context — not here.
 */
function sixMonthTrend(txns, ctx) {
  const now = new Date();
  const buckets = [];
  const excluded = [];
  for (let i = 5; i >= 0; i--) {
    const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const end   = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
    const startStr = isoLocal(start);
    const endStr   = isoLocal(end);
    const inMonth = txns.filter((t) => t.txn_date && t.txn_date >= startStr && t.txn_date < endStr);
    // Phase 1 containment: income/expense/refund/borrowing rules come from
    // lib/interimPolicy.js — the ONE place they are allowed to live.
    const totals = periodTotals(inMonth, (t) =>
      ctx.toBase(Math.abs(Number(t.amount) || 0), ctx.currencyOfAccount(t.account_id)));
    excluded.push(totals.excludedCounts);
    buckets.push({
      ym: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`,
      label: start.toLocaleDateString(undefined, { month: 'short' }),
      income_base: Math.round(totals.income),
      expenses_base: Math.round(totals.expenses),
      surplus_base: Math.round(totals.savings),
    });
  }
  return { buckets, excluded: mergeExcluded(excluded) };
}

export function useDashboardData() {
  const { user } = useAuth();
  const uid = user?.id;
  // Phase 84.22.9 (Wave B2) — currency conversion + account-currency map
  // now come from the app-root CurrencyContext, not per-hook fetches.
  // Every dashboard tile uses the same rates as every other page —
  // no more drift between Money Movement and Insights.
  const currency = useCurrencyContext();
  // Demo mode: URL param opts in/out; localStorage persists.
  const [demo, setDemo] = useState(() => {
    const fromUrl = readUrlParamAndPersist();
    return fromUrl !== null ? fromUrl : isDemoEnabled();
  });

  // Phase 84.5 — stale-while-revalidate. Hydrate initial state from the
  // module-level snapshot cache (if any) so a return-visit to /dashboard
  // renders the last known-good UI instantly instead of a full-page
  // skeleton wall. The background load below still runs and replaces
  // this state once fresh data arrives.
  const cachedInitial = uid ? readSnapshot(uid) : null;

  const [state, setState] = useState(cachedInitial || {
    loading:  true,
    error:    null,
    snapshot: null,
    breakdownThisMonth: null,
    breakdownLastMonth: null,   // now sourced from period=prior_month (Phase 83.1)
    breakdownCategories: [],    // spending-breakdown drawer input (Phase 83.1)
    reconciliation:  { candidates: [], count: 0 },
    recurringSuggestions: [],
    reflectionQuestion:  null,
    latestReflection:    null,
    trend6m:  [],
    trend6mExcluded: null,
    briefing: null,               // Phase 83.2 — HYBRID composed briefing
    demoMode: false,
    computedAt: null,
  });

  const load = useCallback(async () => {
    if (!uid) return;
    // Phase 84.5 — Only surface the full-page skeleton if we don't have
    // a cached snapshot to render behind. Otherwise keep the previous
    // UI visible while the background refresh runs (stale-while-revalidate).
    const hasCached = !!readSnapshot(uid);
    setState((s) => ({
      ...s,
      loading: hasCached ? false : true,
      error: null,
    }));

    // -------------------------------------------------------------------
    // DEMO MODE — return fixture data without touching backend.
    // -------------------------------------------------------------------
    if (demo) {
      setState({
        loading: false, error: null,
        snapshot: DEMO_SNAPSHOT_PATCH,
        breakdownThisMonth: DEMO_BREAKDOWN_THIS_MONTH,
        breakdownLastMonth: DEMO_BREAKDOWN_PRIOR,
        breakdownCategories: (DEMO_BREAKDOWN_THIS_MONTH?.categories) || [],
        reconciliation: { candidates: [], count: 3 },
        recurringSuggestions: [],
        reflectionQuestion: DEMO_REFLECTION_QUESTION,
        latestReflection: null,
        trend6m: DEMO_TREND_6M,
        trend6mExcluded: null,
        briefing: { updates: DEMO_BRIEFING_UPDATES },
        demoAttention: DEMO_ATTENTION_ITEMS,
        demoMode: true,
        computedAt: new Date().toISOString(),
      });
      return;
    }

    try {
      // Six-month transaction window (client-side aggregation for Money
      // Movement). Use isoLocal() so the boundary matches the user's
      // local calendar — same rule the legacy Dashboard.js uses.
      const now = new Date();
      const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);
      const fromStr = isoLocal(sixMonthsAgo);

      // Phase 83.1/83.2 additions: pull the true prior-calendar-month
      // breakdown (for Position Card deltas + Spending Breakdown drawer)
      // and the HYBRID composed briefing (hero + Monthly Reflection
      // prior-month recap + biggest opportunity). Both are non-critical —
      // .catch(() => null) means a partial dashboard still renders if a
      // new endpoint fails or hasn't been deployed yet.
      const [
        snapshot, mThis, mLast, recon, suggestions, questions,
        reflectionCurrent, txnRes, briefingRes,
      ] = await Promise.all([
        authedGet('/api/snapshot'),
        authedGet('/api/dashboard/breakdown?period=month'),
        authedGet('/api/dashboard/breakdown?period=prior_month').catch(() => null),
        authedGet('/api/reconciliation/pending').catch(() => ({ candidates: [], count: 0 })),
        authedGet('/api/recurring/suggestions').catch(() => ({ suggestions: [] })),
        authedGet('/api/questions?source=reflection&status=open&limit=1').catch(() => ({ items: [] })),
        authedGet('/api/weekly-reflections/current').catch(() => null),
        supabase
          .from('transactions')
          .select('id, txn_date, amount, type, category, description, account_id')
          .eq('user_id', uid)
          .gte('txn_date', fromStr)
          .order('txn_date', { ascending: false })
          .limit(5000),
        authedGet('/api/dashboard/briefing').catch(() => null),
      ]);

      const txns = (txnRes && !txnRes.error && Array.isArray(txnRes.data)) ? txnRes.data : [];

      // Phase 84.22.9 (Wave B2) — use the app-root CurrencyContext instead
      // of an inline accounts+FX fetch. Every dashboard number now shares
      // the same rate table as Insights, Recurring, and every other page.
      const trend6m = sixMonthTrend(txns, currency);

      const nextState = {
        loading:  false,
        error:    null,
        snapshot,
        breakdownThisMonth: mThis || null,
        breakdownLastMonth: mLast || null,
        breakdownCategories: (mThis?.categories) || [],
        reconciliation:     recon || { candidates: [], count: 0 },
        recurringSuggestions: (suggestions?.suggestions || suggestions?.items || []),
        reflectionQuestion: (questions?.items || [])[0] || null,
        latestReflection:   reflectionCurrent || null,
        trend6m: trend6m.buckets,
        trend6mExcluded: trend6m.excluded,
        briefing: briefingRes || null,   // shape matches DEMO_BRIEFING_UPDATES wrapper
        demoAttention: null,
        demoMode: false,
        computedAt: new Date().toISOString(),
      };
      setState(nextState);
      // Phase 84.5 — persist so the next re-mount can hydrate instantly.
      writeSnapshot(uid, nextState);
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e }));
    }
  }, [uid, demo, currency]);

  useEffect(() => { load(); }, [load]);

  // Phase 84.22.9 — base_currency now flows from the app-root
  // CurrencyContext. The old snapshot-derived fallback is kept as a
  // second layer of defence in case the /api/currency/context call is
  // still in flight when the dashboard renders (context reports
  // isReady=false → base defaults to 'INR' which the snapshot may
  // correct once loaded).
  const baseCurrency = currency.isReady
    ? currency.base
    : (state.snapshot?.identity?.base_currency
       || state.snapshot?.balance_sheet?.total_assets?.base_currency
       || 'INR');

  return useMemo(() => ({ ...state, baseCurrency, reload: load, demo, setDemo }),
    [state, baseCurrency, load, demo]);
}
